/**
 * What the ribbon does to the document, as ProseMirror commands, and what it shows as pressed
 * (the marks and the paragraph style where the caret is). Pure of the DOM: a command takes the
 * editor's state and a `dispatch`.
 */
import { lift, setBlockType, toggleMark, wrapIn } from "prosemirror-commands";
import type { Mark, MarkType, Node as PMNode, NodeType } from "prosemirror-model";
import { closeHistory } from "prosemirror-history";
import { liftListItem, sinkListItem, wrapInList } from "prosemirror-schema-list";
import { NodeSelection, TextSelection, type Command, type EditorState } from "prosemirror-state";
import { addColumnAfter, addRowAfter, deleteColumn, deleteRow, deleteTable, isInTable } from "prosemirror-tables";
import { docSchema, freshId, MAX_INDENT, pictureLook, type PictureLook } from "../../../state/documents";

const { nodes, marks } = docSchema;

/** The paragraph styles offered, Word's names. */
export const STYLES = [
  { id: "normal", label: "Normal" },
  { id: "title", label: "Title" },
  { id: "h1", label: "Heading 1" },
  { id: "h2", label: "Heading 2" },
  { id: "h3", label: "Heading 3" },
  { id: "quote", label: "Quote" },
] as const;

/** A paragraph style's id. */
export type StyleId = (typeof STYLES)[number]["id"];

/** The style of the block where the caret is. */
export function styleAt(state: EditorState): StyleId {
  const { $from } = state.selection;
  for (let d = $from.depth; d > 0; d--) if ($from.node(d).type === nodes.blockquote) return "quote";
  const block = $from.parent;
  if (block.type === nodes.title) return "title";
  if (block.type === nodes.heading) return `h${Math.min(3, Math.max(1, Number(block.attrs.level) || 1))}` as StyleId;
  return "normal";
}

/** Gives the blocks of the selection the style `style`. */
export function setStyle(style: StyleId): Command {
  return (state, dispatch) => {
    if (style === "quote") return styleAt(state) === "quote" ? lift(state, dispatch) : wrapIn(nodes.blockquote!)(state, dispatch);
    if (styleAt(state) === "quote") lift(state, dispatch);
    const type: NodeType = style === "normal" ? nodes.paragraph! : style === "title" ? nodes.title! : nodes.heading!;
    const attrs = style.startsWith("h") ? { level: Number(style.slice(1)) } : {};
    return setBlockType(type, attrs)(state, dispatch);
  };
}

/** Whether mark `type` is on where the caret is (or over all of the selection). */
export function markActive(state: EditorState, type: MarkType): boolean {
  const { from, $from, to, empty } = state.selection;
  if (empty) return !!type.isInSet(state.storedMarks ?? $from.marks());
  return state.doc.rangeHasMark(from, to, type);
}

/** The mark of `type` where the caret is, if any. */
export function markAt(state: EditorState, type: MarkType): Mark | null {
  const { $from, from, to, empty } = state.selection;
  if (empty) return type.isInSet(state.storedMarks ?? $from.marks()) ?? null;
  let found: Mark | null = null;
  state.doc.nodesBetween(from, to, (node) => {
    found ??= type.isInSet(node.marks) ?? null;
    return !found;
  });
  return found;
}

/** Bold, italic, underline, struck or code on and off. */
export function toggle(name: "strong" | "em" | "underline" | "strike" | "code"): Command {
  return toggleMark(marks[name]!);
}

/** Puts mark `type` with `attrs` on the selection (`null`: takes it off); with no selection, on what is typed next. */
export function setMark(type: MarkType, attrs: Record<string, unknown> | null): Command {
  return (state, dispatch) => {
    const { from, to, empty } = state.selection;
    if (!dispatch) return true;
    const tr = state.tr;
    if (empty) {
      const stored = (state.storedMarks ?? state.selection.$from.marks()).filter((m) => m.type !== type);
      tr.setStoredMarks(attrs ? [...stored, type.create(attrs)] : stored);
    } else {
      tr.removeMark(from, to, type);
      if (attrs) tr.addMark(from, to, type.create(attrs));
    }
    dispatch(tr.scrollIntoView());
    return true;
  };
}

/** Takes the text colour and the highlight off the selection. */
export function clearColours(): Command {
  return (state, dispatch) => {
    const { from, to, empty } = state.selection;
    if (!dispatch) return true;
    const tr = state.tr;
    const gone = [marks.color!, marks.highlight!];
    if (empty) tr.setStoredMarks((state.storedMarks ?? state.selection.$from.marks()).filter((m) => !gone.includes(m.type)));
    else for (const type of gone) tr.removeMark(from, to, type);
    dispatch(tr);
    return true;
  };
}

/** The blocks of text the selection touches, with their positions. */
function textBlocks(state: EditorState): { node: PMNode; pos: number }[] {
  const out: { node: PMNode; pos: number }[] = [];
  const { from, to } = state.selection;
  state.doc.nodesBetween(from, to, (node, pos) => {
    if (node.isTextblock && "align" in node.attrs) out.push({ node, pos });
    return !node.isTextblock;
  });
  return out;
}

/** The alignment where the caret is. */
export function alignAt(state: EditorState): string {
  return (state.selection.$from.parent.attrs.align as string | null) ?? "left";
}

/** Aligns the paragraphs of the selection. */
export function align(to: "left" | "center" | "right" | "justify"): Command {
  return (state, dispatch) => {
    const blocks = textBlocks(state);
    if (!blocks.length) return false;
    if (dispatch) {
      const tr = state.tr;
      for (const { node, pos } of blocks) tr.setNodeMarkup(pos, undefined, { ...node.attrs, align: to === "left" ? null : to });
      dispatch(tr);
    }
    return true;
  };
}

/** Whether the caret is in a list item. */
function inList(state: EditorState): boolean {
  const { $from } = state.selection;
  for (let d = $from.depth; d > 0; d--) if ($from.node(d).type === nodes.list_item) return true;
  return false;
}

/** Moves the selection one step in (`1`) or out (`-1`): a list item into a sub-list, else the paragraph's indent. */
export function indent(by: 1 | -1): Command {
  return (state, dispatch) => {
    if (inList(state)) return (by > 0 ? sinkListItem(nodes.list_item!) : liftListItem(nodes.list_item!))(state, dispatch);
    const blocks = textBlocks(state).filter(({ node }) => node.type !== nodes.title);
    if (!blocks.length) return false;
    if (dispatch) {
      const tr = state.tr;
      for (const { node, pos } of blocks) tr.setNodeMarkup(pos, undefined, { ...node.attrs, indent: Math.max(0, Math.min(MAX_INDENT, (Number(node.attrs.indent) || 0) + by)) });
      dispatch(tr);
    }
    return true;
  };
}

/** The list the caret is in (`null`: none). */
export function listAt(state: EditorState): "bullet_list" | "ordered_list" | null {
  const { $from } = state.selection;
  for (let d = $from.depth; d > 0; d--) {
    const name = $from.node(d).type.name;
    if (name === "bullet_list" || name === "ordered_list") return name;
  }
  return null;
}

/** Makes the selection a list of `kind`, or takes it out of one when it is already. */
export function list(kind: "bullet_list" | "ordered_list"): Command {
  return (state, dispatch) => (listAt(state) === kind ? liftListItem(nodes.list_item!)(state, dispatch) : wrapInList(nodes[kind]!)(state, dispatch));
}

/** Puts `node` where the caret is, as a block of its own, the caret after it (in a new paragraph when nothing follows), as Word does. */
function insertBlock(node: PMNode): Command {
  return (state, dispatch) => {
    if (!dispatch) return true;
    const tr = state.tr.replaceSelectionWith(node);
    let after = tr.selection.to;
    if (!tr.doc.resolve(after).nodeAfter?.isTextblock) {
      tr.insert(after, nodes.paragraph!.create({ id: freshId() }));
    }
    after = Math.min(after + 1, tr.doc.content.size);
    tr.setSelection(TextSelection.near(tr.doc.resolve(after)));
    dispatch(tr.scrollIntoView());
    return true;
  };
}

/** A table of `rows` × `cols` empty cells. */
export function insertTable(rows = 3, cols = 3): Command {
  const cell = () => nodes.table_cell!.createAndFill()!;
  const row = () => nodes.table_row!.create(null, Array.from({ length: cols }, cell));
  return insertBlock(nodes.table!.create({ id: freshId() }, Array.from({ length: rows }, row)));
}

/** What can be done to the table the caret is in. */
export const TABLE_COMMANDS: { id: string; label: string; command: Command }[] = [
  { id: "row", label: "Add a row", command: addRowAfter },
  { id: "column", label: "Add a column", command: addColumnAfter },
  { id: "delete-row", label: "Delete the row", command: deleteRow },
  { id: "delete-column", label: "Delete the column", command: deleteColumn },
  { id: "delete-table", label: "Delete the table", command: deleteTable },
];

/** Whether the caret is in a table. */
export function inTable(state: EditorState): boolean {
  return isInTable(state);
}

/** A picture (or another of the project's files) at the caret. */
export function insertImage(src: string, alt = ""): Command {
  return insertBlock(nodes.image!.create({ id: freshId(), src, alt }));
}

/** The pictures `srcs` as blocks of their own beside the top-level block at `pos`: before it when `before`, else after; the first one selected. */
export function insertImagesAt(pos: number, before: boolean, srcs: readonly string[]): Command {
  return (state, dispatch) => {
    if (!srcs.length) return false;
    if (!dispatch) return true;
    const $pos = state.doc.resolve(Math.max(0, Math.min(pos, state.doc.content.size)));
    const index = $pos.depth ? $pos.index(0) : Math.min($pos.index(0), state.doc.childCount - 1);
    let at = 0;
    for (let i = 0; i < index + (before ? 0 : 1) && i < state.doc.childCount; i++) at += state.doc.child(i).nodeSize;
    const tr = state.tr.insert(at, srcs.map((src) => nodes.image!.create({ id: freshId(), src })));
    dispatch(tr.setSelection(NodeSelection.create(tr.doc, at)).scrollIntoView());
    return true;
  };
}

/** The picture selected, with where it is (`null`: none). */
export function pictureAt(state: EditorState): { pos: number; node: PMNode; look: PictureLook } | null {
  const sel = state.selection;
  if (!(sel instanceof NodeSelection) || sel.node.type !== nodes.image) return null;
  return { pos: sel.from, node: sel.node, look: pictureLook(sel.node.attrs) };
}

/** Changes the picture at `pos` (`alt` its caption, the rest its placement), keeping it selected; one undo of its own. */
export function setPicture(pos: number, patch: Partial<PictureLook> & { alt?: string }): Command {
  return (state, dispatch) => {
    const node = state.doc.nodeAt(pos);
    if (!node || node.type !== nodes.image) return false;
    if (!dispatch) return true;
    const look = pictureLook({ ...node.attrs, ...patch });
    // Taken out of the words or back in: it starts where it was anchored.
    const moved = patch.wrap && patch.wrap !== node.attrs.wrap ? { x: 0, y: 0 } : {};
    const attrs = { ...node.attrs, ...look, ...moved, ...(patch.alt !== undefined ? { alt: patch.alt } : {}) };
    const tr = closeHistory(state.tr).setNodeMarkup(pos, undefined, attrs);
    dispatch(closeHistory(tr.setSelection(NodeSelection.create(tr.doc, pos))));
    return true;
  };
}

/** A page break at the caret: what follows starts a new sheet. */
export function insertPageBreak(): Command {
  return insertBlock(nodes.page_break!.create({ id: freshId() }));
}

/** A mention of node `name` (`id`) at the caret. */
export function insertMention(name: string, id: string | null): Command {
  return (state, dispatch) => {
    if (dispatch) dispatch(state.tr.replaceSelectionWith(nodes.mention!.create({ name, id }), false).insertText(" ").scrollIntoView());
    return true;
  };
}

/** A comment saying `text` on the words selected (`false`: nothing is selected). */
export function addComment(text: string, by: string): Command {
  return (state, dispatch) => {
    const { from, to, empty } = state.selection;
    if (empty || !text.trim()) return false;
    if (dispatch) dispatch(state.tr.addMark(from, to, marks.comment!.create({ id: freshId(), text: text.trim(), by })));
    return true;
  };
}

/** One comment of the document: where its words are, and what it says. */
export interface CommentAt {
  /** Its id. */
  id: string;
  /** What it says. */
  text: string;
  /** Who wrote it. */
  by: string;
  /** The words it is on. */
  quote: string;
  /** Where those words start and end. */
  from: number;
  to: number;
}

/** Every comment of `doc`, in order. */
export function commentsOf(doc: PMNode): CommentAt[] {
  const byId = new Map<string, CommentAt>();
  doc.descendants((node, pos) => {
    if (!node.isText) return true;
    for (const mark of node.marks) {
      if (mark.type !== marks.comment) continue;
      const id = String(mark.attrs.id);
      const known = byId.get(id);
      if (known) {
        known.to = pos + node.nodeSize;
        known.quote += node.text ?? "";
      } else byId.set(id, { id, text: String(mark.attrs.text), by: String(mark.attrs.by), quote: node.text ?? "", from: pos, to: pos + node.nodeSize });
    }
    return false;
  });
  return [...byId.values()];
}

/** Takes comment `id` away (its words stay). */
export function removeComment(id: string): Command {
  return (state, dispatch) => {
    const found = commentsOf(state.doc).find((c) => c.id === id);
    if (!found) return false;
    if (dispatch) {
      const mark = marks.comment!;
      const tr = state.tr;
      state.doc.nodesBetween(found.from, found.to, (node, pos) => {
        const m = node.marks.find((x) => x.type === mark && x.attrs.id === id);
        if (m) tr.removeMark(pos, pos + node.nodeSize, m);
      });
      dispatch(tr);
    }
    return true;
  };
}

/** A heading of the document, for the navigation pane. */
export interface HeadingAt {
  /** Its words. */
  text: string;
  /** 0 for the title, then 1–3. */
  level: number;
  /** Where it starts. */
  pos: number;
}

/** The title and headings of `doc`, in order. */
export function headingsOf(doc: PMNode): HeadingAt[] {
  const out: HeadingAt[] = [];
  doc.forEach((node, pos) => {
    if (node.type === nodes.title) out.push({ text: node.textContent, level: 0, pos });
    else if (node.type === nodes.heading) out.push({ text: node.textContent, level: Number(node.attrs.level) || 1, pos });
  });
  return out.filter((h) => h.text.trim());
}

/** A block a merge kept from the other person (both changed the block before it). */
export interface ConflictAt {
  /** Where it starts. */
  pos: number;
  /** Its words. */
  text: string;
}

/** The other person's versions a merge kept in `doc`. */
export function conflictsOf(doc: PMNode): ConflictAt[] {
  const out: ConflictAt[] = [];
  doc.forEach((node, pos) => {
    if (node.attrs.conflict) out.push({ pos, text: node.textContent });
  });
  return out;
}

/** Settles a conflict at `pos`: keeps theirs (in place of the block before it) or ours (theirs goes). */
export function settle(pos: number, keep: "theirs" | "ours"): Command {
  return (state, dispatch) => {
    const theirs = state.doc.nodeAt(pos);
    if (!theirs?.attrs.conflict) return false;
    if (dispatch) {
      const tr = state.tr;
      if (keep === "ours") tr.delete(pos, pos + theirs.nodeSize);
      else {
        const $pos = state.doc.resolve(pos);
        const before = $pos.nodeBefore;
        tr.setNodeMarkup(pos, undefined, { ...theirs.attrs, conflict: null, id: before?.attrs.id ?? theirs.attrs.id });
        if (before) tr.delete(pos - before.nodeSize, pos);
      }
      dispatch(tr);
    }
    return true;
  };
}

/** Puts the caret at `pos` (a heading, a comment) and shows it. */
export function goTo(pos: number): Command {
  return (state, dispatch) => {
    if (dispatch) dispatch(state.tr.setSelection(TextSelection.near(state.doc.resolve(Math.min(pos + 1, state.doc.content.size)))).scrollIntoView());
    return true;
  };
}
